/**
 * Lazy-loaded FinTech Ops route components with preload support.
 *
 * Preloading on nav-link hover warms the chunk cache so Suspense rarely triggers
 * when the user actually clicks. Each entry exposes `.preload()` returning the
 * import promise (idempotent — React caches the module).
 */
import { ComponentType, lazy } from 'react';

type Loader<T> = () => Promise<{ default: T }>;

interface Preloadable<T extends ComponentType<unknown>> extends React.LazyExoticComponent<T> {
  preload: () => Promise<{ default: T }>;
}

function lazyWithPreload<T extends ComponentType<unknown>>(loader: Loader<T>): Preloadable<T> {
  const Comp = lazy(loader) as Preloadable<T>;
  Comp.preload = loader;
  return Comp;
}

export const LazyRiskRemediatorMain = lazyWithPreload(() =>
  import('src/components/fintech-ops/risk-remediator/RiskRemediator').then((m) => ({ default: m.RiskRemediatorMain as ComponentType<unknown> }))
);
export const LazyUpgradeMedicMain = lazyWithPreload(() =>
  import('src/components/fintech-ops/upgrade-medic/UpgradeMedicPage').then((m) => ({ default: m.UpgradeMedicMain as ComponentType<unknown> }))
);
export const LazyCostMetricsMainPage = lazyWithPreload(() =>
  import('src/components/fintech-ops/cost-metrics/CostMetricsMainPage').then((m) => ({ default: m.CostMetricsMainPage as ComponentType<unknown> }))
);
export const LazyDogmaMain = lazyWithPreload(() =>
  import('src/components/fintech-ops/dogma/DogmaPage').then((m) => ({ default: m.DogmaMain as ComponentType<unknown> }))
);
export const LazyPolicyEnginePage = lazyWithPreload(() =>
  import('src/components/fintech-ops/policy-engine/PolicyEnginePage').then((m) => ({ default: m.PolicyEnginePage as ComponentType<unknown> }))
);
export const LazySasPageMain = lazyWithPreload(() =>
  import('src/components/fintech-ops/sas/SasPage').then((m) => ({ default: m.SasPageMain as ComponentType<unknown> }))
);
export const LazyTicketsPageMain = lazyWithPreload(() =>
  import('src/components/fintech-ops/tickets/TicketsPage').then((m) => ({ default: m.TicketsPageMain as ComponentType<unknown> }))
);
export const LazyUserMetricsPage = lazyWithPreload(() =>
  import('src/components/fintech-ops/user-metrics/UserMetricsPage').then((m) => ({ default: m.UserMetricsPage as ComponentType<unknown> }))
);
export const LazyPipelineOEDashboard = lazyWithPreload(() =>
  import('src/components/fintech-ops/pipeline-oe-dashboard/PipelineOEDashboard').then((m) => ({
    default: m.PipelineOEDashboard as ComponentType<unknown>
  }))
);

export const LazyKingpinPage = lazyWithPreload(() =>
  import('src/components/das-finsuite/kingpin/KingpinPage').then((m) => ({ default: m.KingpinPage as ComponentType<unknown> }))
);

/** Map of route href → preload function. Used by SideNavigation on hover. */
export const ROUTE_PRELOAD_MAP: Record<string, () => Promise<unknown>> = {
  '/operational-excellence/risk-remediator': LazyRiskRemediatorMain.preload,
  '/operational-excellence/upgrade-medic': LazyUpgradeMedicMain.preload,
  '/operational-excellence/cost-metrics': LazyCostMetricsMainPage.preload,
  '/operational-excellence/pipeline-health': LazyDogmaMain.preload,
  '/operational-excellence/policy-engine': LazyPolicyEnginePage.preload,
  '/operational-excellence/security-risks': LazySasPageMain.preload,
  '/operational-excellence/ticket-health': LazyTicketsPageMain.preload,
  '/operational-excellence/user-metrics': LazyUserMetricsPage.preload,
  '/operational-excellence/pipeline-oe-dashboard': LazyPipelineOEDashboard.preload,
  '/kingpin': LazyKingpinPage.preload,
  '/kingpin/goals': LazyKingpinPage.preload,
  '/kingpin/insights': LazyKingpinPage.preload,
  '/kingpin/charts': LazyKingpinPage.preload
};
